/* ---------------------------------------------------------------------------
   The construction report — a department's record over a period.

   Proposal 1 §4 promises FWO weekly, monthly and quarterly reports. This is all
   three: they differ in the days they cover and in nothing else, so they are
   one screen with three presets rather than three documents that would drift.
   It is also the review queue §3.2 promises, and that is deliberate — the
   person who verifies an entry is the person assembling the report it goes
   into, and giving them two screens would mean reviewing in one and finding out
   what they had missed in the other.

   ── WHAT THE SECTIONS ARE, AND WHY THEY ARE NOT OURS TO CHOOSE ──

   **FIDIC Sub-Clause 4.21 specifies the contents of a monthly progress
   report.** FWO will read this against that list, so the headings below are
   its (a) to (h) rather than a shape invented here, in its order, keeping its
   letters. What this register can answer, it answers; what it cannot, it says
   it cannot, by name.

   THAT IS THE WHOLE POINT OF DRAWING THE EMPTY ONES. A monthly report with no
   safety section reads as a report whose author forgot. One that prints
   *Safety statistics — not recorded by this register* reads as a system being
   honest about its inputs, and it is a far better argument for building
   `CM-09` than any roadmap row. Six independent arrivals at that rule are
   listed in STATUS.md; this is the seventh.

   The sections that are not yet answerable name the item that would answer
   them, so a reader who wants one knows it is a decision and not an oversight.

   ── WHAT A REPORT MAY NOT DO ──

   **It may not present an unverified entry as a verified one.** Every row
   carries its state, the rollup leads with how many are unchecked, and the
   printed heading says so where any are. A client reading this has to be able
   to tell what CSI Bangkok has looked at from what arrived an hour ago;
   Proposal 1 sells exactly that distinction.

   **It may not total across units.** One line per unit, always. See
   summariseProgress.

   **It may not claim silence means nothing happened.** A register that could
   not be reached and a period in which nobody built anything are different
   answers, and the screen gives them as different answers.
   --------------------------------------------------------------------------- */

import { h, ICONS } from '../core/dom.ts';
import { actions, can, roleCan, type State } from '../core/store.ts';
import { button, card, cardHead, emptyState, seg, tag } from '../components/widgets.ts';
import { pagehead } from '../components/pagehead.ts';
import {
  PERIOD_LABEL,
  daysBetween,
  nextPeriod,
  periodTitle,
  previousPeriod,
  type PeriodKind,
} from '../data/periods.ts';
import { agency } from '../tenant/agency.ts';
import { paper } from '../components/paper.ts';
import { progressRow } from './works.ts';
import { contractCompletion, forecastOf, varianceSection } from './programme.ts';
import { lookAhead, slipWord, type ForecastRow } from '../data/forecast.ts';
import { trimNumber } from '../data/quantities.ts';
import { formatMoney } from '../tenant/format.ts';
import { active } from '../tenant/registry.ts';
import { mediumDate, prettyDate } from '../data/derive.ts';
import { EXTENSION_STATE_LABEL, grantedDays, type ContractExtension, type ExtensionState } from '../data/contract.ts';
import { findBridge } from '../data/derive.ts';
import { activityLabel } from '../data/activities.ts';
import { documentClause, documentKindLabel } from '../data/documents.ts';
/* TEMPORARY — the FWO demonstration; delete this import and its use in
   sectionA with src/data/fwo-demo.ts. */
import {
  incidentLabel,
  isLostTime,
  plantText,
  safetySummary,
  tradesText,
  weatherLabel,
  weatherObstructs,
} from '../data/siteReturn.ts';
import { ITEM_STATUS_LABEL, testKindLabel, testResultText, testSubject, type ItemStatus } from '../data/siteQuality.ts';
import type { PeriodReport, ProgressEntry, SiteDocument, SiteItem, SiteTest } from '../data/types.ts';

export function worksReportScreen(s: Readonly<State>): HTMLElement {
  return h(
    'div',
    { class: 'wrep' },
    pagehead(s),
    periodBar(s),
    /* On paper (src/components/paper.ts), under the department's head and over
       the foot of every sheet. */
    paper(
      {
        title: reportName(s),
        detail: [findBridge(s.selectedId)?.name || s.selectedId, spanText(s.worksRange.from, s.worksRange.to)]
          .filter(Boolean)
          .join(' · '),
        from: 'Construction monitoring',
        footMid: footMid(s),
      },
      masthead(s),
      /* The resident engineer's own words, first on the page (LC-55). */
      executiveSummary(s),
      /* Printed above the sections so the covering statement is the first thing
         read, on screen and on paper both. */
      standing(s),
      sections(s),
      signOff(),
    ),
  );
}

/* --------------------------- the printed page --------------------------- */

/** "Monthly progress report" — what the paper calls itself, in its masthead and on every sheet. */
function reportName(s: Readonly<State>): string {
  return `${PERIOD_LABEL[s.worksPeriod]} progress report`;
}

/** "1–31 Oct 2026", or "28 Sep 2026 – 4 Oct 2026" across a month's end. */
function spanText(from: string, to: string): string {
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
    ? `${a.getDate()}–${mediumDate(b)}`
    : `${mediumDate(a)} – ${mediumDate(b)}`;
}

/** The foot's middle: how many entries are still in review, or nothing. */
function footMid(s: Readonly<State>): string {
  const unchecked = s.worksRead === 'read' ? s.worksSummary.draft : 0;
  return unchecked > 0 ? `${unchecked} ${unchecked === 1 ? 'entry' : 'entries'} in review` : '';
}

/** The printed report's first lines: which report, of what, for when. On paper only. */
function masthead(s: Readonly<State>): HTMLElement {
  const r = s.worksRange;
  const unchecked = s.worksRead === 'read' ? s.worksSummary.draft : 0;
  const fact = (k: string, v: string, sub: string | null) =>
    h(
      'div',
      { class: 'wrep__fact' },
      h('div', { class: 'wrep__fact-k', text: k }),
      h('div', { class: 'wrep__fact-v', text: v }),
      sub ? h('div', { class: 'wrep__fact-k', text: sub }) : null,
    );
  return h(
    'header',
    { class: 'wrep__masthead' },
    h('div', { class: 'wrep__eyebrow', text: `${reportName(s)} · FIDIC Sub-Clause 4.21` }),
    h('h1', { class: 'wrep__title', text: findBridge(s.selectedId)?.name || s.selectedId }),
    h('div', { class: 'wrep__unit', text: agency().unit }),
    h(
      'div',
      { class: 'wrep__facts' },
      fact('Period', periodTitle(s.worksPeriod, r), `${prettyDate(r.from)} – ${prettyDate(r.to)} · ${daysBetween(r.from, r.to) + 1} days`),
      fact('Structure', s.selectedId, null),
      fact('Entries', String(s.worksSummary.count), `${s.worksSummary.structures} ${s.worksSummary.structures === 1 ? 'structure' : 'structures'}`),
      fact('Issued', mediumDate(new Date()), agency().departmentShort),
    ),
    contractHeading(s),
    unchecked > 0
      ? h('p', {
          class: 'wrep__unchecked',
          text: `${unchecked} of ${s.worksSummary.count} entries are not yet verified, and are marked so where they appear.`,
        })
      : null,
  );
}

/** Who prepared, checked and approved the report — signed by hand, on paper only. */
function signOff(): HTMLElement {
  return h(
    'footer',
    { class: 'wrep__signoff' },
    h(
      'div',
      { class: 'wrep__signers' },
      ...['Prepared by', 'Checked by', 'Approved by'].map((role) =>
        h(
          'div',
          { class: 'wrep__signer' },
          h('div', { class: 'wrep__fact-k', text: role }),
          h('div', { class: 'wrep__sign-line' }),
          h('div', { class: 'wrep__fact-k', text: 'Name and position' }),
          h('div', { class: 'wrep__fact-k', text: 'Date' }),
        ),
      ),
    ),
    h('div', { class: 'wrep__end', text: 'End of report' }),
  );
}

/* --------------------------- choosing the period --------------------------- */

function periodBar(s: Readonly<State>): HTMLElement {
  const kind = s.worksPeriod;
  const range = s.worksRange;

  return h(
    'div',
    { class: 'wrep__bar' },
    seg(
      (['week', 'month', 'quarter', 'custom'] as PeriodKind[]).map((k) => ({
        label: k === 'custom' ? 'Custom' : PERIOD_LABEL[k],
        on: kind === k,
        onClick: () => actions.setWorksPeriod(k),
      })),
    ),

    h('span', { class: 'spacer' }),

    /* Stepping back through history. Disabled for nothing — there is always a
       previous week — and it steps by the period's own length for a custom
       range, which is the only meaning "previous" can have for one. */
    button('Previous', {
      sm: true,
      title: 'The period before this one',
      onClick: () => actions.setWorksRange(previousPeriod(kind, range)),
    }),
    h('span', {
      class: 'wrep__range',
      text: `${range.from} → ${range.to} · ${daysBetween(range.from, range.to) + 1} days`,
    }),
    button('Next', {
      sm: true,
      title: 'The period after this one',
      onClick: () => actions.setWorksRange(nextPeriod(kind, range)),
    }),

    kind === 'custom'
      ? h(
          'span',
          { class: 'wrep__dates' },
          h('input', {
            class: 'input input--sm',
            type: 'date',
            'aria-label': 'First day of the report',
            value: range.from,
            oninput: (e: Event) =>
              actions.setWorksRangeEnd('from', (e.target as HTMLInputElement).value),
          }),
          h('input', {
            class: 'input input--sm',
            type: 'date',
            'aria-label': 'Last day of the report',
            value: range.to,
            oninput: (e: Event) =>
              actions.setWorksRangeEnd('to', (e.target as HTMLInputElement).value),
          }),
        )
      : null,

    (() => {
      const print = button('Print', {
        sm: true,
        iconPath: ICONS.print,
        title: 'Print this report, or save it as a PDF',
        onClick: () => window.print(),
      });
      print.classList.add('wrep__print');
      return print;
    })(),
  );
}

/* --------------------------- the contract --------------------------- */

/**
 * The contract the structure in hand is administered under (LC-42), read from
 * the baseline in force: who, which contract, how much, when it began and when
 * it must be finished, with the extensions granted by the period's end. Absent
 * where no baseline carries a contract.
 */
export function contractHeading(s: Readonly<State>): HTMLElement | null {
  const c = s.baseline?.contract ?? null;
  if (c === null) return null;
  const asOf = s.worksRange.to;
  const granted = grantedDays(s.extensions.filter((e) => (e.decidedAt ?? '').slice(0, 10) <= asOf));
  const own = active().currency;
  const value =
    c.value === null
      ? null
      : c.currency === '' || c.currency === own.code
        ? formatMoney(c.value, own)
        : `${c.currency} ${c.value.toLocaleString('en')}`;
  const parts = [
    c.contractNo ? `Contract ${c.contractNo}` : 'Contract',
    c.contractor || null,
    value,
    `commenced ${prettyDate(c.commencement)}`,
    `${c.completionDays} days${granted ? ` + ${granted} extension` : ''}`,
    `completion ${prettyDate(contractCompletion(s, asOf))}`,
  ].filter((x): x is string => !!x);
  return h('div', { class: 'wrep__contract', 'data-contract': c.contractNo, text: parts.join(' · ') });
}

/* --------------------------- the executive summary --------------------------- */

/** Whether this person may write the report's summary and choose its photographs. */
function mayWriteReport(s: Readonly<State>): boolean {
  return s.backend === 'live' && !!s.selectedId && (roleCan(s, 'works.supervise') || roleCan(s, 'progress.review'));
}

/** The structure in hand's report for the period, where it is the one read. */
function reportFor(s: Readonly<State>): PeriodReport | null {
  const r = s.worksReport;
  return r && r.bridgeId === s.selectedId && r.from === s.worksRange.from && r.to === s.worksRange.to ? r : null;
}

/**
 * The executive summary (LC-55): what the resident engineer writes about the
 * period, per structure, printed at the top. Written by whoever supervises the
 * works or reviews the record.
 */
export function executiveSummary(s: Readonly<State>): HTMLElement | null {
  const report = reportFor(s);
  const may = mayWriteReport(s);
  const draft = s.worksSummaryDraft;
  if (!report?.summary && !may) return null;
  return card(
    { class: report?.summary ? 'wrep__summary wrep__summary--written' : 'wrep__summary' },
    cardHead(
      'Executive summary',
      report?.updatedBy ? `${findBridge(s.selectedId)?.name ?? s.selectedId} · ${report.updatedBy}` : findBridge(s.selectedId)?.name ?? undefined,
      may && draft === null
        ? button(report?.summary ? 'Edit' : 'Write', { sm: true, onClick: () => actions.editWorksSummary() })
        : null,
    ),
    draft !== null
      ? h(
          'div',
          { style: 'padding:0 14px 12px' },
          h('textarea', {
            id: 'wrep-summary',
            class: 'input',
            rows: 6,
            value: draft,
            oninput: (e: Event) => actions.setWorksSummaryDraft((e.target as HTMLTextAreaElement).value),
          }),
          s.worksReportError ? h('p', { class: 'dlg__error', role: 'alert', text: s.worksReportError }) : null,
          h(
            'div',
            { style: 'display:flex;gap:6px;margin-top:8px' },
            button('Save', { sm: true, primary: true, onClick: () => void actions.saveWorksReport() }),
            button('Cancel', { sm: true, onClick: () => actions.cancelWorksSummary() }),
          ),
        )
      : report?.summary
        ? h('div', { class: 'wrep__summary-text', style: 'padding:0 14px 12px;white-space:pre-wrap', text: report.summary })
        : emptyState('No summary written for this period.'),
  );
}

/* --------------------------- the covering statement --------------------------- */

/** A warning above the sections when the register could not be read; nothing otherwise. */
function standing(s: Readonly<State>): HTMLElement | null {
  if (s.worksRead === 'unreachable') {
    return h(
      'div',
      { class: 'wrep__standing wrep__standing--warn' },
      h('p', {
        text: 'The register did not answer.',
      }),
    );
  }

  if (s.worksRead === 'unknown') {
    return h(
      'div',
      { class: 'wrep__standing wrep__standing--warn' },
      h('p', {
        text: 'Not connected to the live register.',
      }),
    );
  }

  return null;
}

/* --------------------------- the eight sections --------------------------- */

function sections(s: Readonly<State>): HTMLElement {
  return h(
    'div',
    { class: 'wrep__sections' },
    sectionA(s),
    sectionB(s),
    sectionC(s),
    sectionD(s),
    sectionE(s),
    sectionF(s),
    sectionG(s),
    /* As at the period's end (LC-51), not today. */
    varianceSection(s, findBridge(s.selectedId)?.name ?? s.selectedId, false, s.worksRange.to),
    lookAheadSection(s),
  );
}

/**
 * The look-ahead (LC-55): what the forecast has under way as the period ends,
 * and what it starts over as many days again — the Monitoring panel's *Planned
 * now* and *Next*, from the re-solved dates.
 */
export function lookAheadSection(s: Readonly<State>): HTMLElement | null {
  const asOf = s.worksRange.to;
  if (s.baseline === null || s.baseline.takenAt.slice(0, 10) > asOf) return null;
  const f = forecastOf(s, asOf);
  if (f === null) return null;
  const days = daysBetween(s.worksRange.from, s.worksRange.to) + 1;
  const ahead = lookAhead(f, days);
  const table = (rows: readonly ForecastRow[], none: string, kind: string) =>
    rows.length === 0
      ? h('p', { class: 'wrep__gap', text: none })
      : h(
          'table',
          { class: 'tbl', 'data-ahead': kind },
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { style: 'padding-left:14px', text: 'Activity' }),
              h('th', { class: 'num', text: 'Forecast' }),
              h('th', { class: 'num', text: 'Planned' }),
              h('th', { class: 'num', text: 'Slip' }),
            ),
          ),
          h(
            'tbody',
            {},
            ...rows.slice(0, 20).map((r) =>
              h(
                'tr',
                { 'data-activity': r.activityId },
                h('td', { style: 'padding-left:14px', text: r.name }, r.critical ? tag('critical', 3) : null),
                h('td', { class: 'num', text: `${prettyDate(r.forecastStart)} → ${prettyDate(r.forecastFinish)}` }),
                h('td', { class: 'num', text: `${prettyDate(r.plannedStart)} → ${prettyDate(r.plannedFinish)}` }),
                h('td', { class: 'num', text: slipWord(r.slipDays) }),
              ),
            ),
          ),
        );
  return card(
    { class: 'wrep__ahead' },
    cardHead('Look-ahead', `${prettyDate(ahead.from)} → ${prettyDate(ahead.to)} · as forecast at ${prettyDate(asOf)}`),
    h('div', { class: 'works__sub-head', text: `Under way at the period’s end · ${ahead.underway.length}` }),
    table(ahead.underway, 'Nothing is under way at the period’s end.', 'underway'),
    h('div', { class: 'works__sub-head', text: `Starting next · ${ahead.starting.length}` }),
    table(ahead.starting, 'Nothing is forecast to start in the next period.', 'starting'),
  );
}

/** (a) Progress, by structure. */
function sectionA(s: Readonly<State>): HTMLElement {
  const byBridge = groupByBridge(s.worksEvents);
  const unchecked = s.worksSummary.draft;

  return card(
    {},
    cardHead(
      '(a) Progress on site',
      s.worksSummary.count > 0
        ? `${s.worksSummary.count} entries · ${s.worksSummary.structures} structures`
        : undefined,
      /* An unverified entry never reads as a verified one, on paper included. */
      ...[
        unchecked > 0 ? tag(`${unchecked} not verified`, 3) : null,
      ].filter((el) => el !== null),
    ),
    s.worksRead !== 'read'
      ? emptyState('Not read — see the statement above.')
      : s.worksEvents.length === 0
        ? emptyState('No work was recorded in this period.')
        : h(
            'div',
            {},
            toDateTable(s),
            ...byBridge.map(([bridgeId, events]) => bridgeTable(bridgeId, events, s)),
          ),
  );
}

/**
 * This period's quantities beside the totals to date (LC-55), per structure,
 * activity and unit — never added across units, and never across activities
 * either: an excavation and a pour both in cubic metres are two lines.
 */
export function quantitiesToDate(
  period: readonly ProgressEntry[],
  toDate: readonly ProgressEntry[],
): { bridgeId: string; activity: string; unit: string; period: number; toDate: number }[] {
  const key = (e: ProgressEntry) => `${e.bridgeId}\u0001${e.activity}\u0001${e.unit}`;
  const out = new Map<string, { bridgeId: string; activity: string; unit: string; period: number; toDate: number }>();
  const bridges = new Set(period.map((e) => e.bridgeId));
  const add = (e: ProgressEntry, field: 'period' | 'toDate') => {
    if (e.quantity === null || e.unit === null || e.pending) return;
    const k = key(e);
    const row = out.get(k) ?? { bridgeId: e.bridgeId, activity: e.activity, unit: e.unit, period: 0, toDate: 0 };
    row[field] += e.quantity;
    out.set(k, row);
  };
  for (const e of period) add(e, 'period');
  for (const e of toDate) if (bridges.has(e.bridgeId)) add(e, 'toDate');
  return [...out.values()].sort(
    (a, b) => a.bridgeId.localeCompare(b.bridgeId) || activityLabel(a.activity).localeCompare(activityLabel(b.activity)) || a.unit.localeCompare(b.unit),
  );
}

function toDateTable(s: Readonly<State>): HTMLElement | null {
  if (s.worksToDate === null) {
    return h('p', { class: 'wrep__gap', text: 'The totals to date could not be read.' });
  }
  const rows = quantitiesToDate(s.worksEvents, s.worksToDate);
  if (!rows.length) return null;
  return h(
    'div',
    { 'data-todate': String(rows.length) },
    h('div', { class: 'works__sub-head', text: `Quantities, this period and to ${prettyDate(s.worksRange.to)}` }),
    h(
      'table',
      { class: 'tbl' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { style: 'padding-left:14px', text: 'Activity' }),
          h('th', { text: 'Structure' }),
          h('th', { class: 'num', text: 'This period' }),
          h('th', { class: 'num', text: 'To date' }),
        ),
      ),
      h(
        'tbody',
        {},
        ...rows.map((r) =>
          h(
            'tr',
            { 'data-qty': `${r.activity}|${r.unit}` },
            h('td', { style: 'padding-left:14px', text: activityLabel(r.activity) }),
            h('td', { class: 'display', text: r.bridgeId }),
            h('td', { class: 'num', text: `${trimNumber(r.period)} ${r.unit}` }),
            h('td', { class: 'num', text: `${trimNumber(r.toDate)} ${r.unit}` }),
          ),
        ),
      ),
    ),
  );
}

/**
 * One table per structure.
 *
 * GROUPED RATHER THAN ONE FLAT LIST, because the question a monthly report is
 * read with is "what happened on each bridge", not "what happened in the
 * department in date order". On a single-structure contract — which is what
 * Proposal 1 is — this produces exactly one table and costs nothing.
 */
function bridgeTable(bridgeId: string, events: ProgressEntry[], s: Readonly<State>): HTMLElement {
  const mayReview = can(s, 'progress.review');
  return h(
    'div',
    { class: 'wrep__bridge' },
    h('div', { class: 'wrep__bridge-head', text: bridgeId }),
    h(
      'table',
      { class: 'tbl' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { style: 'padding-left:14px', text: 'Activity' }),
          h('th', { text: 'Where' }),
          h('th', { class: 'num', text: 'Quantity' }),
          h('th', { class: 'num', text: 'Done on' }),
          h('th', { text: 'State' }),
          h('th', { text: '' }),
        ),
      ),
      h('tbody', {}, ...events.map((e) => progressRow(e, { mayReview, refused: s.refused }))),
    ),
  );
}

/** How many photographs a report prints until somebody chooses. */
export const DEFAULT_REPORT_PHOTOS = 12;

/**
 * The photographs a report prints (LC-55): the ones chosen for it, in the order
 * chosen, or the newest twelve where nobody has chosen.
 */
export function reportPhotos(withPhotos: readonly ProgressEntry[], chosen: readonly string[] | null): ProgressEntry[] {
  if (chosen === null) {
    return [...withPhotos].sort((a, b) => b.occurredOn.localeCompare(a.occurredOn) || a.id.localeCompare(b.id)).slice(0, DEFAULT_REPORT_PHOTOS);
  }
  const byId = new Map(withPhotos.map((e) => [e.id, e]));
  return chosen.map((id) => byId.get(id)).filter((e): e is ProgressEntry => e !== undefined);
}

/** (b) Photographs. */
function sectionB(s: Readonly<State>): HTMLElement {
  const all = s.worksEvents.filter((e) => e.hasPhoto);
  const picking = s.worksPhotoPick;
  const withPhotos = reportPhotos(all, reportFor(s)?.photoIds ?? null);

  return card(
    {},
    cardHead(
      '(b) Photographs',
      all.length > 0 ? `${withPhotos.length} printed · ${all.length} taken` : undefined,
      mayWriteReport(s) && all.length > 0 && picking === null
        ? button('Choose photographs', { sm: true, onClick: () => actions.openPhotoPick(withPhotos.map((e) => e.id)) })
        : null,
    ),
    picking !== null ? photoPicker(all, picking, s.worksReportError) : null,
    withPhotos.length === 0
      ? emptyState(
          s.worksEvents.length === 0
            ? 'No entries, and so no photographs.'
            : 'No entry in this period carries a photograph.',
        )
      : h(
          'div',
          { class: 'wrep__plate' },
          ...withPhotos.map((e) =>
            h(
              'figure',
              { class: 'wrep__fig' },
              h('img', {
                class: 'wrep__img',
                src: photoSrc(e),
                loading: 'lazy',
                alt: `${activityLabel(e.activity)} on ${e.occurredOn}`,
              }),
              h('figcaption', {
                class: 'wrep__cap',
                text: `${e.bridgeId} · ${activityLabel(e.activity)} · ${e.occurredOn}`,
              }),
            ),
          ),
        ),
  );
}

/** (e) Quality records — what the library's four non-work entries carry. */
/** Every photograph of the period, each with a box saying whether the report prints it. */
function photoPicker(all: readonly ProgressEntry[], picked: readonly string[], error: string | null): HTMLElement {
  return h(
    'div',
    { class: 'wrep__pick', 'data-picked': String(picked.length) },
    h(
      'div',
      { class: 'wrep__plate' },
      ...all.map((e) =>
        h(
          'label',
          { class: 'wrep__fig', style: 'cursor:pointer' },
          h('img', { class: 'wrep__img', src: photoSrc(e), loading: 'lazy', alt: `${activityLabel(e.activity)} on ${e.occurredOn}` }),
          h(
            'span',
            { class: 'wrep__cap' },
            h('input', {
              type: 'checkbox',
              'data-photo': e.id,
              checked: picked.includes(e.id),
              onchange: () => actions.togglePhotoPick(e.id),
            }),
            ` ${e.bridgeId} · ${activityLabel(e.activity)} · ${e.occurredOn}`,
          ),
        ),
      ),
    ),
    error ? h('p', { class: 'dlg__error', role: 'alert', text: error }) : null,
    h(
      'div',
      { style: 'display:flex;gap:6px;padding:0 14px 12px' },
      button(`Print these ${picked.length}`, { sm: true, primary: true, onClick: () => void actions.saveWorksReport() }),
      button('Cancel', { sm: true, onClick: () => actions.cancelPhotoPick() }),
    ),
  );
}

function sectionE(s: Readonly<State>): HTMLElement {
  /* The four entries in the library that are not work: a hold point released, a
     survey check, a non-conformance raised, a stoppage. They are what makes the
     record a construction record rather than a production tally, and they are
     the only part of 4.21(e) this register can currently answer. */
  const quality = s.worksEvents.filter((e) =>
    ['hold-point', 'survey-check', 'non-conformance', 'stoppage', 'pile-test'].includes(e.activity),
  );

  /* The documents that answer (e): test certificates, material certificates,
     method statements, survey records, non-conformance close-outs. */
  const certificates = s.worksDocuments.filter((d) => documentClause(d.kind) === 'e');
  const tests = s.worksTests;
  const failed = tests.filter((t) => !t.passed).length;
  const items = s.worksItems;

  return card(
    {},
    cardHead(
      '(e) Quality records, checks and events',
      quality.length || certificates.length || tests.length || items.length
        ? [
            `${quality.length} checks`,
            tests.length ? `${tests.length} tests, ${failed} failed` : null,
            items.length ? `${items.length} HP/NCR/RFI` : null,
            `${certificates.length} filed`,
          ]
            .filter(Boolean)
            .join(' · ')
        : undefined,
    ),
    quality.length === 0
      ? emptyState('No hold point, survey check, test, non-conformance or stoppage was recorded.')
      : h(
          'table',
          { class: 'tbl' },
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { style: 'padding-left:14px', text: 'Event' }),
              h('th', { text: 'Structure' }),
              h('th', { class: 'num', text: 'Date' }),
              h('th', { text: 'What was recorded' }),
            ),
          ),
          h(
            'tbody',
            {},
            ...quality.map((e) =>
              h(
                'tr',
                {},
                h('td', { style: 'padding-left:14px', text: activityLabel(e.activity) }),
                h('td', { class: 'display', text: e.bridgeId }),
                h('td', { class: 'num', text: e.occurredOn }),
                /* The note IS the record for these five. A hold point with no
                   note says only that one was released, which is why the
                   library's own entries ask for who released it. */
                h('td', {}, e.note ? h('span', { text: e.note }) : h('span', { class: 'muted', text: 'No note' })),
              ),
            ),
          ),
        ),
    /* The results as figures (LC-53), and the register over the period (LC-54),
       each item's status as it stood at the period's end. */
    tests.length ? h('div', {}, h('div', { class: 'works__sub-head', text: 'Test results' }), reportTests(tests)) : null,
    items.length
      ? h('div', {}, h('div', { class: 'works__sub-head', text: 'Hold points, NCRs and RFIs' }), reportItems(items, s.worksRange.to))
      : null,
    /* The certificates themselves, which is the other half of (e) and the half
       that was missing until documents existed. Drawn under the checks rather
       than beside them because the reading order is "what was checked, and
       here is the paper". */
    certificates.length > 0
      ? h(
          'div',
          {},
          h('div', { class: 'works__sub-head', text: 'Certificates and records filed' }),
          documentTable(certificates),
        )
      : null,
  );
}

function reportTests(tests: readonly SiteTest[]): HTMLElement {
  return h(
    'table',
    { class: 'tbl' },
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { style: 'padding-left:14px', text: 'Test' }),
        h('th', { text: 'Structure' }),
        h('th', { text: 'Of' }),
        h('th', { text: 'Result' }),
        h('th', { class: 'num', text: 'Tested' }),
        h('th', { text: '' }),
      ),
    ),
    h(
      'tbody',
      {},
      ...tests.map((t) =>
        h(
          'tr',
          {},
          h('td', { style: 'padding-left:14px', text: testKindLabel(t.kind) }),
          h('td', { class: 'display', text: t.bridgeId }),
          h('td', { text: testSubject(t) || '—' }),
          h('td', { text: testResultText(t) }),
          h('td', { class: 'num', text: t.testedOn }),
          h('td', { text: t.passed ? 'Pass' : `Fail${t.ncrRef ? ` · ${t.ncrRef}` : ''}` }),
        ),
      ),
    ),
  );
}

/** An item's status on a day: closed or answered only if it was by then. */
function statusAt(i: SiteItem, day: string): ItemStatus {
  if (i.closedAt && i.closedAt.slice(0, 10) <= day) return 'closed';
  if (i.answeredAt && i.answeredAt.slice(0, 10) <= day) return 'answered';
  return 'open';
}

function reportItems(items: readonly SiteItem[], periodEnd: string): HTMLElement {
  return h(
    'table',
    { class: 'tbl' },
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { style: 'padding-left:14px', text: 'Ref' }),
        h('th', { text: 'Structure' }),
        h('th', { text: 'Title' }),
        h('th', { class: 'num', text: 'Raised' }),
        h('th', { text: 'At period end' }),
      ),
    ),
    h(
      'tbody',
      {},
      ...items.map((i) =>
        h(
          'tr',
          {},
          h('td', { style: 'padding-left:14px', class: 'display', text: i.ref }),
          h('td', { class: 'display', text: i.bridgeId }),
          h('td', { text: i.title }),
          h('td', { class: 'num', text: i.raisedAt.slice(0, 10) }),
          h('td', { text: ITEM_STATUS_LABEL[statusAt(i, periodEnd)] }),
        ),
      ),
    ),
  );
}

/** (c) Manufacture and delivery of main items. */
function sectionC(s: Readonly<State>): HTMLElement {
  /* Delivery notes are the only thing this register can answer (c) with, and
     the section says so rather than implying it holds a procurement schedule.
     What is genuinely absent — order dates, manufacture progress, expected
     shipment — is named underneath, because a reader comparing this document
     against the contract needs to see the gap stated. */
  const deliveries = s.worksDocuments.filter((d) => documentClause(d.kind) === 'c');
  /* The main items as the site diary records them: girders made in the yard,
     and girders, bearings and joints put into the structure. */
  const made = s.worksEvents.filter((e) => MAIN_ITEM_ACTIVITIES.has(e.activity));

  return card(
    {},
    cardHead(
      '(c) Manufacture and delivery of main items',
      made.length || deliveries.length
        ? [made.length ? `${made.length} entries` : null, deliveries.length ? `${deliveries.length} delivered` : null]
            .filter(Boolean)
            .join(' · ')
        : undefined,
    ),
    made.length === 0 && deliveries.length === 0
      ? emptyState('No delivery of a main item was recorded in this period.')
      : null,
    made.length ? mainItemTable(made) : null,
    deliveries.length
      ? h('div', {}, made.length ? h('div', { class: 'works__sub-head', text: 'Delivery notes' }) : null, documentTable(deliveries))
      : null,
  );
}

/** Work on the main items FIDIC 4.21(c) asks after. */
const MAIN_ITEM_ACTIVITIES: ReadonlySet<string> = new Set([
  'girder-stressed',
  'girder-cast',
  'girder-erected',
  'bearing-set',
  'joint-installed',
]);

function mainItemTable(entries: readonly ProgressEntry[]): HTMLElement {
  return h(
    'table',
    { class: 'tbl', 'data-main-items': String(entries.length) },
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { style: 'padding-left:14px', text: 'Item' }),
        h('th', { text: 'Structure' }),
        h('th', { text: 'Where' }),
        h('th', { class: 'num', text: 'Quantity' }),
        h('th', { class: 'num', text: 'Date' }),
        h('th', { text: 'What was recorded' }),
      ),
    ),
    h(
      'tbody',
      {},
      ...[...entries]
        .sort((a, b) => a.occurredOn.localeCompare(b.occurredOn) || a.id.localeCompare(b.id))
        .map((e) =>
          h(
            'tr',
            {},
            h('td', { style: 'padding-left:14px', text: activityLabel(e.activity) }),
            h('td', { class: 'display', text: e.bridgeId }),
            h('td', { text: e.component ?? '—' }),
            h('td', { class: 'num', text: e.quantity === null ? '—' : `${trimNumber(e.quantity)} ${e.unit ?? ''}`.trim() }),
            h('td', { class: 'num', text: e.occurredOn }),
            h('td', {}, e.note ? h('span', { text: e.note }) : h('span', { class: 'muted', text: 'No note' })),
          ),
        ),
    ),
  );
}

/**
 * (f) Notices of claims: the extensions of time claimed on the structure in
 * hand that were open at some point in the period, each as it stood at the end.
 */
function sectionF(s: Readonly<State>): HTMLElement {
  const { from, to } = s.worksRange;
  const claims = s.extensions
    .filter((e) => e.raisedAt.slice(0, 10) <= to && (e.decidedAt === null || e.decidedAt.slice(0, 10) >= from))
    .sort((a, b) => a.raisedAt.localeCompare(b.raisedAt));
  const stateAt = (e: ContractExtension): ExtensionState =>
    e.decidedAt !== null && e.decidedAt.slice(0, 10) <= to ? e.state : 'proposed';
  const open = claims.filter((e) => stateAt(e) === 'proposed');

  return card(
    {},
    cardHead(
      '(f) Notices of Employer’s and Contractor’s claims',
      claims.length
        ? `${claims.length} claims · ${open.length} undecided, ${open.reduce((n, e) => n + e.days, 0)} days`
        : undefined,
    ),
    claims.length === 0
      ? emptyState('No claim was open in this period.')
      : h(
          'table',
          { class: 'tbl', 'data-claims': String(claims.length) },
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { style: 'padding-left:14px', text: 'Claim' }),
              h('th', { text: 'Structure' }),
              h('th', { class: 'num', text: 'Days' }),
              h('th', { class: 'num', text: 'Notified' }),
              h('th', { class: 'num', text: 'Decided' }),
              h('th', { text: 'At period end' }),
            ),
          ),
          h(
            'tbody',
            {},
            ...claims.map((e) => {
              const st = stateAt(e);
              return h(
                'tr',
                {},
                h('td', { style: 'padding-left:14px', text: `Extension of time — ${e.reason}` }),
                h('td', { class: 'display', text: e.bridgeId }),
                h('td', { class: 'num', text: String(e.days) }),
                h('td', { class: 'num', text: e.raisedAt.slice(0, 10) }),
                h('td', { class: 'num', text: st === 'proposed' ? '—' : e.decidedAt!.slice(0, 10) }),
                h('td', {}, tag(st === 'proposed' ? 'Undecided' : EXTENSION_STATE_LABEL[st], st === 'rejected' ? 3 : st === 'proposed' ? 2 : 1)),
              );
            }),
          ),
        ),
  );
}

/** (d) The Contractor's personnel and equipment, from the daily returns. */
function sectionD(s: Readonly<State>): HTMLElement {
  const returns = s.worksReturns;
  const days = daysBetween(s.worksRange.from, s.worksRange.to) + 1;
  const counted = returns.filter((r) => r.labour !== null);
  const peak = counted.reduce((max, r) => Math.max(max, r.labour ?? 0), 0);
  const idle = counted.filter((r) => r.labour === 0).length;
  const lost = returns.filter((r) => weatherObstructs(r.weather)).length;
  /* LC-56: rain as measured, and plant hours working and idle over the period. */
  const rained = returns.filter((r) => r.rainfallMm != null);
  const rain = rained.reduce((n, r) => n + (r.rainfallMm ?? 0), 0);
  const plantHours = returns.flatMap((r) => r.plantRows ?? []);
  const working = plantHours.reduce((n, p) => n + (p.workingHours ?? 0), 0);
  const idleHours = plantHours.reduce((n, p) => n + (p.idleHours ?? 0), 0);

  return card(
    {},
    cardHead(
      '(d) Contractor’s personnel and equipment',
      returns.length ? `${returns.length} of ${days} days returned` : undefined,
    ),
    returns.length === 0
      ? emptyState(
          s.worksRead === 'read'
            ? 'No daily return was recorded in this period.'
            : 'Not read — see the statement above.',
        )
      : h(
          'div',
          {},
          h(
            'div',
            { class: 'works__totals' },
            h('span', { class: 'works__stat', text: `${returns.length} of ${days} days returned` }),
            /* PEAK AND NOT AVERAGE. An average over days that include
               unreturned ones is a number with an undeclared denominator, and
               the figure a reader actually wants from a monthly report is how
               many people the contractor had on site at the busiest point. */
            counted.length
              ? h('span', { class: 'works__stat', text: `peak ${peak} on site` })
              : h('span', { class: 'works__stat', text: 'nobody counted on any day' }),
            idle > 0
              ? h('span', {
                  class: 'works__stat works__stat--draft',
                  text: `${idle} day${idle === 1 ? '' : 's'} with nobody on site`,
                })
              : null,
            lost > 0
              ? h('span', {
                  class: 'works__stat works__stat--draft',
                  text: `${lost} day${lost === 1 ? '' : 's'} of obstructive weather`,
                })
              : null,
            rained.length
              ? h('span', { class: 'works__stat', 'data-rain': String(rain), text: `${trimNumber(rain)} mm rain over ${rained.length} days measured` })
              : null,
            plantHours.length
              ? h('span', {
                  class: idleHours > 0 ? 'works__stat works__stat--draft' : 'works__stat',
                  'data-plant': `${working}|${idleHours}`,
                  text: `plant ${trimNumber(working)} h working · ${trimNumber(idleHours)} h idle`,
                })
              : null,
          ),
          h(
            'table',
            { class: 'tbl' },
            h(
              'thead',
              {},
              h(
                'tr',
                {},
                h('th', { style: 'padding-left:14px', text: 'Day' }),
                h('th', { text: 'Structure' }),
                h('th', { text: 'Weather' }),
                h('th', { class: 'num', text: 'On site' }),
                h('th', { text: 'Trades' }),
                h('th', { text: 'Plant' }),
                h('th', { class: 'num', text: 'Rain' }),
                h('th', { class: 'num', text: 'Hours' }),
              ),
            ),
            h(
              'tbody',
              {},
              ...returns.map((r) =>
                h(
                  'tr',
                  {},
                  h('td', { style: 'padding-left:14px', text: r.returnedOn }),
                  h('td', { class: 'display', text: r.bridgeId }),
                  h('td', { text: weatherLabel(r.weather) }),
                  h('td', { class: 'num', text: r.labour === null ? '—' : String(r.labour) }),
                  h('td', { text: tradesText(r) }),
                  h('td', { text: plantText(r) }),
                  h('td', { class: 'num', text: r.rainfallMm == null ? '—' : `${trimNumber(r.rainfallMm)} mm` }),
                  h('td', { class: 'num', text: r.hours === null ? '—' : String(r.hours) }),
                ),
              ),
            ),
          ),
        ),
  );
}

/** (g) Safety statistics, hazardous incidents and environmental matters. */
function sectionG(s: Readonly<State>): HTMLElement {
  const incidents = s.worksIncidents;
  const summary = safetySummary(incidents, s.worksReturns);

  return card(
    {},
    cardHead(
      '(g) Safety statistics, incidents and environmental matters',
      incidents.length ? `${incidents.length} recorded` : undefined,
    ),

    h(
      'div',
      { class: 'works__totals' },
      /* ZERO IS PRINTED, and printing it is the whole value of this section.
         "0 lost-time injuries" is a statistic; an absent section is not, and a
         client cannot tell it from a register nobody used. */
      h('span', {
        class: summary.lostTime > 0 ? 'works__stat works__stat--draft' : 'works__stat',
        text: `${summary.lostTime} lost-time`,
      }),
      h('span', { class: 'works__stat', text: `${summary.total} recorded in total` }),
      /* LC-56: the days lost, where the incidents say. */
      incidents.some((i) => i.daysLost != null)
        ? h('span', {
            class: 'works__stat',
            'data-days-lost': String(incidents.reduce((n, i) => n + (i.daysLost ?? 0), 0)),
            text: `${trimNumber(incidents.reduce((n, i) => n + (i.daysLost ?? 0), 0))} days lost`,
          })
        : null,
      ...Object.entries(summary.byKind).map(([kind, n]) =>
        h('span', { class: 'works__stat', text: `${n} ${incidentLabel(kind).toLowerCase()}` }),
      ),
    ),

    incidents.length === 0
      ? emptyState(
          s.worksRead === 'read'
            ? 'No incident was recorded in this period.'
            : 'Not read — see the statement above.',
        )
      : h(
          'table',
          { class: 'tbl' },
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { style: 'padding-left:14px', text: 'What' }),
              h('th', { text: 'Structure' }),
              h('th', { class: 'num', text: 'When' }),
              h('th', { text: 'Description' }),
              h('th', { class: 'num', text: 'People' }),
              h('th', { class: 'num', text: 'Days lost' }),
              h('th', { text: 'Action' }),
            ),
          ),
          h(
            'tbody',
            {},
            ...incidents.map((i) =>
              h(
                'tr',
                {},
                h('td', { style: 'padding-left:14px' }, tag(incidentLabel(i.kind), isLostTime(i.kind) ? 3 : 2)),
                h('td', { class: 'display', text: i.bridgeId }),
                h('td', { class: 'num', text: i.occurredOn }),
                h('td', { text: i.description }),
                h('td', {
                  class: 'num',
                  title: i.peopleNames || undefined,
                  text: i.peopleInvolved == null ? '—' : `${i.peopleInvolved}${i.peopleNames ? ` · ${i.peopleNames}` : ''}`,
                }),
                h('td', { class: 'num', text: i.daysLost == null ? '—' : trimNumber(i.daysLost) }),
                h('td', { text: i.closed ? i.action || 'Closed' : `Open. ${i.action}`.trim() }),
              ),
            ),
          ),
        ),
  );
}

/**
 * Documents, as a table.
 *
 * Shared by (c) and (e) because a delivery note and a test certificate are
 * read the same way: what it is, when it is dated, and a link to it. The kind
 * column is what distinguishes them, and it is drawn in both.
 */
function documentTable(docs: readonly SiteDocument[]): HTMLElement {
  return h(
    'table',
    { class: 'tbl' },
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { style: 'padding-left:14px', text: 'Title' }),
        h('th', { text: 'Kind' }),
        h('th', { text: 'Structure' }),
        h('th', { class: 'num', text: 'Dated' }),
      ),
    ),
    h(
      'tbody',
      {},
      ...docs.map((d) =>
        h(
          'tr',
          {},
          h(
            'td',
            { style: 'padding-left:14px' },
            h(
              'a',
              {
                href: documentHref(d),
                target: '_blank',
                rel: 'noopener',
                title: d.note || d.title,
              },
              d.title,
            ),
          ),
          h('td', { text: documentKindLabel(d.kind) }),
          h('td', { class: 'display', text: d.bridgeId }),
          /* A document with no date on it says so rather than borrowing the
             day it was filed — the period query already falls back to that,
             and printing it here would hide the fallback. */
          h('td', { class: 'num', text: d.datedOn ?? '—' }),
        ),
      ),
    ),
  );
}

function documentHref(d: SiteDocument): string {
  const query = new URLSearchParams({ bridgeId: d.bridgeId, documentId: d.id });
  return `/api/site-document?${query}`;
}

/* --------------------------- helpers --------------------------- */

function photoSrc(e: ProgressEntry): string {
  /* TEMPORARY — the FWO demonstration; delete this line with
     src/data/fwo-demo.ts. A fabricated entry has no row and so no object in the
     bucket, and carries its own drawing instead. */
  if (e.photoUrl) return e.photoUrl;

  /* Built here rather than imported from works.ts, because the report shows a
     plate of photographs across structures and each one is scoped by its own
     entry's bridge. */
  const query = new URLSearchParams({ bridgeId: e.bridgeId, eventId: e.id });
  return `/api/progress-photo?${query}`;
}

/**
 * Entries grouped by structure, each group keeping the newest-first order the
 * endpoint returned.
 *
 * A Map preserves insertion order, so the structures come out in the order
 * their most recent entry appeared — which puts the busiest site first without
 * anything having to sort.
 */
function groupByBridge(events: readonly ProgressEntry[]): [string, ProgressEntry[]][] {
  const out = new Map<string, ProgressEntry[]>();
  for (const e of events) {
    const list = out.get(e.bridgeId);
    if (list) list.push(e);
    else out.set(e.bridgeId, [e]);
  }
  return [...out.entries()];
}
